import { useEffect, useMemo, useState, useCallback } from 'react';
import { Play, AlertTriangle, CheckCircle2, MapPin, Download } from 'lucide-react';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';

import PeriodPicker from '../../components/mec/PeriodPicker';
import PerShopTable from '../../components/mec/PerShopTable';
import { usePeriodState } from '../../components/mec/usePeriodState';

import { listRouting, runClose, getClose } from '../../api/mec';
import { formatMoney } from '../../utils/formatters';
import { rowsToCsv, downloadCsv, centsToDollarsRaw } from '../../components/mec/csvHelpers';

// MEC Locations (View 2 per MEC_REQUIREMENTS.md §7) — sortable one-row-per-shop
// breakdown across the selected period. This is essentially a focused view of
// the Dashboard's per-shop table, sans top-level summary cards. PerShopTable
// already implements the sort logic, so we lean on it.

function normalizeCloseResult(raw) {
  if (!raw) return null;
  if (raw.parts) return raw;
  const a_total = raw.a_total_cents ?? ((raw.a_used_cents || 0) + (raw.a_pending_return_cents || 0));
  const b_total = raw.b_total_cents ?? ((raw.b_received_cents || 0) + (raw.b_pending_return_cents || 0));
  return {
    close_run_id: raw.close_run_id,
    shop_id: raw.shop_id,
    period_start: raw.period_start,
    period_end_inclusive: raw.period_end_inclusive,
    status: raw.status,
    created_at: raw.created_at,
    finalized_at: raw.finalized_at,
    parts: {
      a_used_cents: raw.a_used_cents || 0,
      a_pending_return_cents: raw.a_pending_return_cents || 0,
      a_total_cents: a_total,
      b_received_cents: raw.b_received_cents || 0,
      b_pending_return_cents: raw.b_pending_return_cents || 0,
      b_total_cents: b_total,
      b0_cents: raw.b0_cents || 0,
      delta_wip_cents: raw.delta_wip_cents ?? (b_total - (raw.b0_cents || 0)),
      p_cents: raw.p_cents || 0,
      variance_cents: raw.variance_cents || 0,
      match_tally: {},
    },
    sublet: null,
  };
}

export default function Locations() {
  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(true);
  const [routingError, setRoutingError] = useState(null);

  // Period state: URL ?start=&end= → localStorage → defaults.
  const { periodStart, periodEnd, setPeriod } = usePeriodState('_locations');

  const [closesByShop, setClosesByShop] = useState({});
  const [loadingShopIds, setLoadingShopIds] = useState([]);
  const [runningShopIds, setRunningShopIds] = useState([]);
  const [bannerToast, setBannerToast] = useState(null);

  // Load shops (active 6 — hide 7515).
  useEffect(() => {
    let alive = true;
    setRoutingLoading(true);
    setRoutingError(null);
    listRouting()
      .then((data) => {
        if (!alive) return;
        setRouting((data?.shops || []).filter((s) => s.shop_id !== 7515));
      })
      .catch((err) => {
        if (!alive) return;
        setRoutingError(err?.message || 'Failed to load shop list');
      })
      .finally(() => alive && setRoutingLoading(false));
    return () => { alive = false; };
  }, []);

  // Cache-check close runs for every shop in the period.
  useEffect(() => {
    if (!routing.length || !periodStart || !periodEnd) {
      setClosesByShop({});
      return;
    }
    let alive = true;
    setClosesByShop({});
    setLoadingShopIds(routing.map((s) => s.shop_id));
    Promise.allSettled(
      routing.map((s) =>
        getClose(s.shop_id, periodStart, periodEnd)
          .then((data) => ({ shop_id: s.shop_id, data }))
          .catch((err) => {
            if (/404|not found/i.test(err?.message || '')) {
              return { shop_id: s.shop_id, data: null };
            }
            return { shop_id: s.shop_id, error: err?.message || 'Failed to load' };
          })
      )
    ).then((results) => {
      if (!alive) return;
      const next = {};
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const { shop_id, data, error } = r.value;
          if (error) next[shop_id] = { error };
          else if (data) next[shop_id] = normalizeCloseResult(data);
        }
      }
      setClosesByShop(next);
      setLoadingShopIds([]);
    });
    return () => { alive = false; };
  }, [routing, periodStart, periodEnd]);

  const runAll = useCallback(async () => {
    if (!routing.length) return;
    setBannerToast(null);
    setRunningShopIds(routing.map((s) => s.shop_id));
    const tally = { ok: 0, err: 0 };
    await Promise.all(
      routing.map(async (s) => {
        try {
          const result = await runClose(s.shop_id, periodStart, periodEnd, false);
          setClosesByShop((prev) => ({ ...prev, [s.shop_id]: normalizeCloseResult(result) }));
          tally.ok += 1;
        } catch (err) {
          setClosesByShop((prev) => ({
            ...prev,
            [s.shop_id]: { error: err?.message || 'Close run failed' },
          }));
          tally.err += 1;
        } finally {
          setRunningShopIds((prev) => prev.filter((id) => id !== s.shop_id));
        }
      })
    );
    setBannerToast({
      kind: tally.err > 0 ? (tally.ok > 0 ? 'mixed' : 'error') : 'success',
      message:
        tally.err > 0
          ? `${tally.ok} closed, ${tally.err} failed.`
          : `All ${tally.ok} closes ran successfully.`,
    });
    setTimeout(() => setBannerToast(null), 8000);
  }, [routing, periodStart, periodEnd]);

  // Build per-shop rows for CSV export (same shape PerShopTable computes).
  const exportCsv = () => {
    const rows = routing.map((s) => {
      const r = closesByShop[s.shop_id];
      const parts = r?.parts || null;
      const p = parts?.p_cents || 0;
      const a = parts?.a_total_cents != null
        ? parts.a_total_cents
        : (parts?.a_used_cents || 0) + (parts?.a_pending_return_cents || 0);
      const dWip = parts?.delta_wip_cents || 0;
      const variance = parts?.variance_cents != null ? parts.variance_cents : p - a - dWip;
      const variancePct = p > 0 ? (variance / p) * 100 : 0;
      return {
        shop_name: s.shop_name || `Shop ${s.shop_id}`,
        shop_id: s.shop_id,
        p_cents: p,
        a_cents: a,
        delta_wip_cents: dWip,
        variance_cents: variance,
        variance_pct: variancePct.toFixed(2),
        last_run: r?.created_at || r?.finalized_at || '',
        status: r?.status || (parts ? 'draft' : 'no_run'),
      };
    });
    const csv = rowsToCsv(rows, [
      { key: 'shop_name', label: 'Shop' },
      { key: 'shop_id', label: 'Shop ID' },
      { key: 'p_cents', label: 'Purchases (P)', accessor: (r) => centsToDollarsRaw(r.p_cents) },
      { key: 'a_cents', label: 'Matched (A)', accessor: (r) => centsToDollarsRaw(r.a_cents) },
      { key: 'delta_wip_cents', label: 'Delta WIP', accessor: (r) => centsToDollarsRaw(r.delta_wip_cents) },
      { key: 'variance_cents', label: 'Variance', accessor: (r) => centsToDollarsRaw(r.variance_cents) },
      { key: 'variance_pct', label: 'Variance %' },
      { key: 'last_run', label: 'Last Run' },
      { key: 'status', label: 'Status' },
    ]);
    downloadCsv(`mec-locations-${periodStart}-to-${periodEnd}.csv`, csv);
  };

  const someRunning = runningShopIds.length > 0;
  const someData = Object.values(closesByShop).some((r) => r?.parts);

  // Aggregate quick summary for the period.
  const summary = useMemo(() => {
    const acc = { p: 0, a: 0, variance: 0, shops_with_data: 0 };
    for (const s of routing) {
      const r = closesByShop[s.shop_id];
      if (!r?.parts) continue;
      acc.shops_with_data += 1;
      acc.p += r.parts.p_cents || 0;
      acc.a += r.parts.a_total_cents
        || (r.parts.a_used_cents || 0) + (r.parts.a_pending_return_cents || 0);
      acc.variance += r.parts.variance_cents || 0;
    }
    return acc;
  }, [closesByShop, routing]);

  return (
    <div>
      <PageHeader
        title="Locations"
        description="Per-shop variance breakdown for the selected period. Click any row to drill into a location's matched ROs, WIP, and Tier 1/2/3 detail."
        breadcrumbs={[
          { label: 'Month-End Close', href: '/mec' },
          { label: 'Locations' },
        ]}
        actions={
          <>
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Download size={15} />}
              onClick={exportCsv}
              disabled={!someData}
            >
              Export CSV
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={someRunning ? null : <Play size={15} />}
              loading={someRunning}
              onClick={runAll}
              disabled={routing.length === 0 || someRunning}
            >
              {someRunning
                ? `Running ${runningShopIds.length} of ${routing.length}…`
                : `Run closes for ${routing.length} shop${routing.length === 1 ? '' : 's'}`}
            </Button>
          </>
        }
      />

      {routingError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[14px] font-semibold">Couldn't load shop list.</div>
              <div className="text-[12px] mt-1 text-[var(--color-warm-600)]">{routingError}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Period selector — Locations always shows ALL active shops, no shop filter */}
      <Card className="mb-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <PeriodPicker start={periodStart} end={periodEnd} onChange={(s, e) => setPeriod(s, e)} />
          <div className="flex items-center gap-3 text-[12px] text-[var(--color-warm-500)]">
            <div className="inline-flex items-center gap-1.5">
              <MapPin size={13} />
              {routing.length} active shop{routing.length === 1 ? '' : 's'}
            </div>
            <span className="text-[var(--color-warm-300)]">·</span>
            <div>
              {summary.shops_with_data} with data
            </div>
          </div>
        </div>
      </Card>

      {bannerToast && (
        <div className={`
          mb-5 p-3 rounded-[var(--radius-md)] text-[13px] flex items-start gap-2
          ${bannerToast.kind === 'success'
            ? 'bg-[var(--color-success-50)] border border-[var(--color-success-100)] text-[var(--color-success-700)]'
            : bannerToast.kind === 'mixed'
              ? 'bg-[var(--color-action-50)] border border-[var(--color-action-100)] text-[var(--color-action-700)]'
              : 'bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[var(--color-danger-700)]'}
        `}>
          {bannerToast.kind === 'success' ? (
            <CheckCircle2 size={15} className="shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          )}
          <div>{bannerToast.message}</div>
        </div>
      )}

      {/* Org-wide summary band */}
      {someData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <SummaryStat label="Active shops" value={routing.length} />
          <SummaryStat
            label="Total purchases"
            value={formatMoney(summary.p / 100)}
            mono
          />
          <SummaryStat
            label="Total matched"
            value={formatMoney(summary.a / 100)}
            mono
          />
          <SummaryStat
            label="Net variance"
            value={(summary.variance >= 0 ? '+' : '') + formatMoney(summary.variance / 100)}
            mono
            tone={summary.variance > 0 ? 'leakage' : summary.variance < 0 ? 'recovery' : 'neutral'}
          />
        </div>
      )}

      <PerShopTable
        rows={closesByShop}
        selectedShops={routing}
        loading={routingLoading || loadingShopIds.length > 0}
        runningShopIds={runningShopIds}
      />

      <div className="mt-6 flex items-start gap-2 text-[11px] text-[var(--color-warm-500)]">
        <Badge tone="cream" size="xs">Tip</Badge>
        <span>
          Sort any column by clicking the header. Variance % &gt; 5% shows action-tone, &gt; 10% shows danger-tone.
        </span>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, mono = false, tone = 'neutral' }) {
  const valColor = {
    neutral:  'text-[var(--color-warm-900)]',
    leakage:  'text-[var(--color-danger-700)]',
    recovery: 'text-[var(--color-recovery-700)]',
  }[tone];
  return (
    <Card className="!p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] mb-1.5">
        {label}
      </div>
      <div className={`text-[18px] font-extrabold tracking-tight leading-none ${mono ? 'tabular-nums' : ''} ${valColor}`}>
        {value}
      </div>
    </Card>
  );
}
